import { deepmerge } from '@mui/utils';
import type { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import type { Device } from './components';
import { createComponents } from './components';
import { palette } from './palette';
import { createTypography } from './typography';

export type { Device } from './components';

export const FONT_PRELOADS = [];

// extend types to satisfy styleguide: https://app.zeplin.io/project/5d493736066e193158b2d910/dashboard
declare module '@mui/material/styles' {
  // extend palette
  interface Palette {
    additional?: Palette['primary'];
    sale?: Palette['primary'];
    sustainability?: Palette['primary'];
    payback?: Palette['primary'];
    joe?: Palette['primary'];
  }

  interface PaletteOptions {
    additional?: PaletteOptions['primary'];
    sale?: PaletteOptions['primary'];
    sustainability?: PaletteOptions['primary'];
    payback?: PaletteOptions['primary'];
    joe?: PaletteOptions['primary'];
  }

  interface PaletteColor {
    textTransparent?: string;
  }

  interface SimplePaletteColorOptions {
    textTransparent?: string;
  }

  interface TypeText {
    dark: string;
    darkTransparent: string;
    light: string;
    lightTransparent: string;
  }

  interface TypographyVariants {
    body3: CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: CSSProperties;
  }

  interface FontStyleOptions {
    fontWeightBlack: number;
  }

  interface FontStyleOverrides {
    fontWeightBlack: true;
  }
}

declare module '@mui/material' {
  interface ButtonPropsColorOverrides {
    sale: true;
    additional: true;
  }

  export interface Color {
    main?: string;
    light?: string;
    dark?: string;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    subtitle1: false;
    subtitle2: false;
    button: false;
    caption: false;
    overline: false;
  }
  interface FontStyleOverrides {
    fontWeightBlack: true;
    fontWeightSemiBold: true;
  }
}

declare module '@mui/material/styles/createTypography' {
  interface FontStyle {
    fontWeightBlack: number;
    fontWeightSemiBold: number;
  }
}

declare module '@mui/material/Slider' {
  interface SliderPropsColorOverrides {
    additional: true;
  }
}

/** Define theme object and create a theme instance
 * See here for more info: https://mui.com/customization/theming/#api */
const breakpointsOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 360,
      md: 600,
      lg: 900,
      xl: 1140,
    },
  },
};
const breakpointsTheme = createTheme(breakpointsOptions);

const { up } = breakpointsTheme.breakpoints!;

const themeOptions = (device: Device): ThemeOptions => ({
  spacing: (factor: number) => `${factor * 0.5}rem`,
  palette,
  typography: createTypography(up!),
  components: createComponents(device),
  // TODO: fill or overwrite types
  shadows: [
    'none',
    '0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14)',
    '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)',
    '0 1px 8px 0 rgba(0, 0, 0, 0.2), 0 3px 3px -2px rgba(0, 0, 0, 0.12), 0 3px 4px 0 rgba(0, 0, 0, 0.14)',
    '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 4px 5px 0 rgba(0, 0, 0, 0.14)',
    'none',
    '0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14)',
    'none',
    '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14)',
    '0 5px 6px -3px rgba(0, 0, 0, 0.2), 0 3px 16px 2px rgba(0, 0, 0, 0.12), 0 9px 12px 1px rgba(0, 0, 0, 0.14)',
    'none',
    'none',
    '0 7px 8px -4px rgba(0, 0, 0, 0.2), 0 5px 22px 4px rgba(0, 0, 0, 0.12), 0 12px 17px 2px rgba(0, 0, 0, 0.14)',
    'none',
    'none',
    'none',
    '0 8px 10px -5px rgba(0, 0, 0, 0.2), 0 6px 30px 5px rgba(0, 0, 0, 0.12), 0 16px 24px 2px rgba(0, 0, 0, 0.14)',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    '0 11px 15px -7px rgba(0, 0, 0, 0.2), 0 9px 46px 8px rgba(0, 0, 0, 0.12), 0 24px 38px 3px rgba(0, 0, 0, 0.14)',
  ],
});

export const generateDefaultThemeOptions = (device: Device) =>
  deepmerge(breakpointsOptions, themeOptions(device));
const theme = (device: Device) => createTheme(generateDefaultThemeOptions(device));

// eslint-disable-next-line import/no-default-export
export default theme;
